/**
 * Redacted logging for clinical mutation failures (#2247).
 *
 * Error paths used to log full request payloads (medication names, doses,
 * appointment notes, treatment details) and raw API error bodies, which can
 * echo the rejected values back. Console output is not a clinical record:
 * diagnostics get the endpoint, status, error name/code, request id, and
 * zod-style issue codes with FIELD PATHS ONLY — never field values and never
 * free-text messages (validation messages embed the offending input).
 *
 * A static guard spec (clinical-log-redaction.spec.ts) keeps new
 * payload-shaped arguments out of console calls in clinical mutation code.
 */

export type RedactedClinicalError = {
  endpoint: string;
  status?: number;
  errorName?: string;
  errorCode?: string;
  requestId?: string;
  /** "code:field.path" pairs — field names only, never values. */
  issues?: string[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function pickString(
  record: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value) return value;
  }
  return undefined;
}

function pickNumber(
  record: Record<string, unknown>,
  ...keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return undefined;
}

export function redactClinicalApiError(
  endpoint: string,
  error: unknown,
): RedactedClinicalError {
  const redacted: RedactedClinicalError = { endpoint };
  const record = asRecord(error);
  if (!record) {
    if (error instanceof Error) redacted.errorName = error.name;
    return redacted;
  }

  redacted.status = pickNumber(record, "status", "statusCode");
  redacted.errorName = pickString(record, "error", "name");
  redacted.errorCode = pickString(record, "code");
  redacted.requestId = pickString(record, "request_id", "requestId");

  const issues = record.issues;
  if (Array.isArray(issues)) {
    const safe = issues
      .map((issue) => {
        const item = asRecord(issue);
        if (!item) return null;
        const code = pickString(item, "code") ?? "issue";
        const path = Array.isArray(item.path)
          ? item.path.map(String).join(".")
          : "";
        return path ? `${code}:${path}` : code;
      })
      .filter((entry): entry is string => entry !== null);
    if (safe.length > 0) redacted.issues = safe;
  }

  return redacted;
}

/**
 * Log a clinical mutation failure without the request payload or raw error
 * body. `tag` is the hook/component marker (e.g. "[useCreateMedication]").
 */
export function logClinicalMutationError(
  tag: string,
  endpoint: string,
  error: unknown,
): void {
  console.error(tag, "request failed:", redactClinicalApiError(endpoint, error));
}
